import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import LinkIcon from "@mui/icons-material/Link";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import { generateLink, download } from "../../features/slices/Download";

const DownloadPage: React.FC = () => {
  const [path, setPath] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [downloadLink, setDownloadLink] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch<AppDispatch>();

  const generateDownloadLink = async () => {
    setLoading(true);
    setError(null);
    setDownloadLink(null);

    try {
      const response = await dispatch(
        generateLink({
          path,
        })
      ).unwrap();

      if (response.download_link) {
        setDownloadLink(response.download_link);
        setToken(response.token);
      } else {
        setError("Failed to generate download link");
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || "Error generating download link");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (token) {
      try {
        const response = await dispatch(download(token)).unwrap();

        const fileName = path.split("\\").pop() || "download.zip";

        const blob = new Blob([response], { type: "application/zip" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        setError("Error downloading file");
      }
    }
  };

  return (
    <Box sx={{ maxWidth: 760, mx: "auto", py: { xs: 1, sm: 3 } }}>
      <Stack spacing={0.5} sx={{ mb: 2 }}>
        <Typography component="h1" sx={{ fontSize: { xs: 22, sm: 28 }, fontWeight: 800 }}>
          Download artifact
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: 14 }}>
          Generate a short-lived link for an existing zip in DeviceSure storage.
        </Typography>
      </Stack>

      <Card variant="outlined" sx={{ borderRadius: 1 }}>
        <CardContent>
          <Stack spacing={2}>
            <TextField
              label="Zip file path"
              value={path}
              onChange={(e) => setPath(e.target.value)}
              fullWidth
              placeholder="/suite/artifacts/..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <InsertDriveFileOutlinedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={generateDownloadLink}
                disabled={loading || !path.trim()}
                startIcon={loading ? undefined : <LinkIcon />}
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                {loading ? <CircularProgress color="inherit" size={22} /> : "Generate link"}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {downloadLink && (
        <Card variant="outlined" sx={{ borderRadius: 1, mt: 2 }}>
          <CardContent>
            <Stack spacing={2}>
          <Alert severity="success">
                Download link generated.
          </Alert>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleDownload}
                startIcon={<DownloadIcon />}
                sx={{ alignSelf: { xs: "stretch", sm: "flex-start" } }}
          >
                Download file
          </Button>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default DownloadPage;
